import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLeaf, 
  faClock, 
  faMapMarkerAlt, 
  faExternalLinkAlt,
  faShoppingBag,
  faUtensils,
  faCrown,
  faPaw,
  faPhone,
  faCalendarAlt,
  faTimes,
  faInfoCircle,
  faCheck,
  faBook,
  faGlobe,
  faSpa
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import { useRetailSection } from '../../../hooks/useRetailSection';
import Button from '../../../components/Button/Button';

const farmShopImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/farmshop1.webp';
const duchessImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/Duchess%20Collection/BelvoirJune26-SamChurchill-213.webp';

// Import images (replace with actual shop images)

const belvoirBooksImage = 'https://BelvoirCastle.b-cdn.net/Books/thousand-years.webp';
const wellnessImage = 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%203%20(1).webp';
const WELLNESS_SITE_URL = 'https://www.belvoirwellness.com/';

const retailBanner = 'https://BelvoirCastle.b-cdn.net/Retail%20village/Belvoir%20retail%20village-3.webp';


const ShopsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const Hero = styled.section`
  position: relative;
  min-height: calc(100dvh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
  padding-bottom: 8rem;
  color: #fff;
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(16, 29, 64, 0.9) 0%,
    rgba(16, 29, 64, 0.5) 34%,
    rgba(16, 29, 64, 0.12) 64%,
    rgba(16, 29, 64, 0.2) 100%
  );
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: clamp(2.5rem, 6vw, 4.5rem) 1.5rem clamp(3rem, 7vw, 4.75rem);
  text-align: center;
`;

const HeroCopy = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 0 2.35rem;
  padding: 2.4rem 2.5rem 1.6rem;

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    left: 50%;
    top: 48%;
    width: min(52rem, 94vw);
    height: 160%;
    transform: translate(-50%, -50%);
    background: radial-gradient(
      ellipse at center,
      rgba(8, 14, 32, 0.78) 0%,
      rgba(8, 14, 32, 0.55) 36%,
      rgba(8, 14, 32, 0.22) 62%,
      rgba(8, 14, 32, 0) 78%
    );
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.85rem;
  max-width: 12ch;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(3.4rem, 8vw, 6rem);
  font-weight: 500;
  line-height: 0.95;
  color: #fff;
  text-shadow:
    0 1px 1px rgba(0, 0, 0, 0.85),
    0 4px 10px rgba(0, 0, 0, 0.7),
    0 14px 28px rgba(0, 0, 0, 0.55);
`;

const HeroSubtitle = styled.p`
  margin: 0;
  max-width: 36rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: clamp(0.69rem, 1.2vw, 0.8125rem);
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  line-height: 1.7;
  color: #fff;
  text-shadow:
    0 1px 1px rgba(0, 0, 0, 0.85),
    0 4px 10px rgba(0, 0, 0, 0.7),
    0 14px 28px rgba(0, 0, 0, 0.55);
`;

const ExploreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.1rem;
  padding: 0.9rem 1.7rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: transparent;
  color: #fff;
  text-decoration: none;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2.5rem 2rem 4rem;
  scroll-margin-top: 200px;
`;

const retailStatusColor = ({ $status }) => {
  if ($status === 'open') return '#2e7d4f';
  if ($status === 'soon') return '#c47a1a';
  return '#b42318';
};

const retailStatusWash = ({ $status }) => {
  if ($status === 'open') return '#f3faf5';
  if ($status === 'soon') return '#fff8ef';
  return '#fdf4f3';
};

const CurrentTimeDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  width: min(100%, 28rem);
  margin: 0 auto 2.5rem;
  padding: 0.95rem 1.2rem;
  background: ${retailStatusWash};
  border: 1px solid ${props => `${retailStatusColor(props)}33`};
  border-radius: 16px;
  box-shadow: 0 10px 28px rgba(16, 29, 64, 0.07);
`;

const TimeStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
`;

const StatusDot = styled.span`
  flex: 0 0 auto;
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  background: ${retailStatusColor};
  box-shadow: 0 0 0 5px ${props => `${retailStatusColor(props)}22`};
`;

const StatusCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

const StatusLabel = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${retailStatusColor};
`;

const StatusNote = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.82rem;
  line-height: 1.3;
  color: ${props => props.theme.colors.secondary};
`;

const TimeValue = styled.strong`
  flex: 0 0 auto;
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  font-weight: 500;
  line-height: 1;
  color: ${retailStatusColor};
`;

const FeaturedShops = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-bottom: 6rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const OtherShops = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ShopCard = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FeaturedShopCard = styled(ShopCard)``;

const ShopImage = styled.div`
  width: 100%;
  height: 260px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$fit === 'contain' ? '#e7e1d8' : props.theme.colors.offWhite};

  img {
    width: ${props => props.$fit === 'contain' ? 'auto' : '100%'};
    height: ${props => props.$fit === 'contain' ? 'calc(100% - 36px)' : '100%'};
    max-width: ${props => props.$fit === 'contain' ? 'calc(100% - 48px)' : 'none'};
    object-fit: ${props => props.$fit || 'cover'};
    object-position: ${props => props.$position || 'center'};
    display: block;
    box-shadow: ${props => props.$fit === 'contain' ? '0 12px 28px rgba(16, 29, 64, 0.22)' : 'none'};
  }
`;

const FeaturedShopImage = styled(ShopImage)`
  height: 320px;
`;

const ShopContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ShopTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ShopDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.secondary};
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: ${props => {
    if (props.$comingSoon) return 'rgba(157, 109, 73, 0.92)';
    if (props.isOpen) return 'rgba(76, 175, 80, 0.85)';
    if (props.openingSoon) return 'rgba(255, 165, 0, 0.85)';
    return 'rgba(244, 67, 54, 0.85)';
  }};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(4px);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  
  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const PhoneLink = styled.a`
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  font-weight: 600;
`;

const ShopFooter = styled.div`
  margin-top: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  button {
    width: 100%;
    background-color: ${props => props.theme.colors.accent};
    color: white;
    border: 2px solid transparent;
    border-radius: 50px;
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: transparent;
      color: ${props => props.theme.colors.accent};
      border-color: ${props => props.theme.colors.accent};
      transform: translateY(-2px);
    }
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const UpcomingDatesSection = styled.div`
  margin: 4rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const UpcomingDatesTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const DatesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const DateCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid ${props => {
    if (props.$type === 'christmas') return 'rgba(220, 20, 60, 0.8)';
    if (props.$type === 'closed') return 'rgba(244, 67, 54, 0.8)';
    if (props.$type === 'special') return 'rgba(255, 165, 0, 0.8)';
    return 'rgba(34, 139, 34, 0.8)';
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const DateTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateRange = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.8rem;
`;

const DateDetails = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const DateNote = styled.div`
  font-size: 0.9rem;
  font-style: italic;
  color: ${props => props.theme.colors.secondary};
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => props.theme.colors.greyBackground};
`;

const ShopsOverview = () => {
  const navigate = useNavigate();
  useRetailSection();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const getRetailVillageStatus = (now) => {
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    const closedAllDay =
      (year === 2025 && month === 11 && (day === 25 || day === 26)) ||
      (year === 2026 && month === 0 && day === 1);

    if (closedAllDay) {
      return { status: 'closed', label: 'Closed', note: 'Retail Village is closed today' };
    }

    const time = now.getHours() * 100 + now.getMinutes();
    if (time >= 900 && time < 1700) {
      return { status: 'open', label: 'Open now', note: 'Retail Village · until 5:00pm' };
    }
    if (time >= 800 && time < 900) {
      return { status: 'soon', label: 'Opening soon', note: 'Retail Village · opens at 9:00am' };
    }
    if (time >= 1700) {
      return { status: 'closed', label: 'Closed', note: 'Retail Village · opens 9:00am tomorrow' };
    }
    return { status: 'closed', label: 'Closed', note: 'Retail Village · opens at 9:00am' };
  };

  const retailVillage = getRetailVillageStatus(currentTime);

  const isShopOpen = (openTime, closeTime, currentTime, closedOn = [], shopId = '') => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    
    // Check if today is a closed day
    if (closedOn.includes(currentTime.getDay())) {
      return { isOpen: false, openingSoon: false };
    }
    
    // Christmas Day - All shops closed
    if (year === 2025 && month === 11 && day === 25) {
      return { isOpen: false, openingSoon: false };
    }
    
    // Boxing Day (Dec 26, 2025) - Farm Shop and Duchess Collection: 9am-3:30pm
    if (year === 2025 && month === 11 && day === 26) {
      if (shopId === 'farm-shop' || shopId === 'duchess-collection') {
        const currentTimeNum = currentTime.getHours() * 100 + currentTime.getMinutes();
        const openTimeNum = 9 * 100; // 9:00
        const closeTimeNum = 15 * 100 + 30; // 15:30
        const isOpen = currentTimeNum >= openTimeNum && currentTimeNum < closeTimeNum;
        const oneHourBeforeOpen = openTimeNum - 100;
        const openingSoon = currentTimeNum >= oneHourBeforeOpen && currentTimeNum < openTimeNum;
        return { isOpen, openingSoon };
      }
    }
    
    // New Year's Day (Jan 1, 2026) - Farm Shop and Duchess Collection: 9am-3:30pm
    if (year === 2026 && month === 0 && day === 1) {
      if (shopId === 'farm-shop' || shopId === 'duchess-collection') {
        const currentTimeNum = currentTime.getHours() * 100 + currentTime.getMinutes();
        const openTimeNum = 9 * 100; // 9:00
        const closeTimeNum = 15 * 100 + 30; // 15:30
        const isOpen = currentTimeNum >= openTimeNum && currentTimeNum < closeTimeNum;
        const oneHourBeforeOpen = openTimeNum - 100;
        const openingSoon = currentTimeNum >= oneHourBeforeOpen && currentTimeNum < openTimeNum;
        return { isOpen, openingSoon };
      }
    }
    
    const [openHour, openMinute] = openTime.split(':').map(Number);
    const [closeHour, closeMinute] = closeTime.split(':').map(Number);
    const currentTimeNum = currentTime.getHours() * 100 + currentTime.getMinutes();
    const openTimeNum = openHour * 100 + openMinute;
    const closeTimeNum = closeHour * 100 + closeMinute;
    const isOpen = currentTimeNum >= openTimeNum && currentTimeNum < closeTimeNum;
    const oneHourBeforeOpen = openTimeNum - 100;
    const openingSoon = currentTimeNum >= oneHourBeforeOpen && currentTimeNum < openTimeNum;
    return { isOpen, openingSoon };
  };

  const featuredShops = [
    {
      id: 'farm-shop',
      title: 'Farm Shop',
      icon: faLeaf,
      image: farmShopImage,
      imagePosition: 'center 40%',
      description: 'Experience the finest local produce direct from our estate and carefully selected suppliers. Our Farm Shop offers fresh seasonal vegetables, artisanal cheeses, local meats, and specialty foods.',
      openTime: '09:00',
      closeTime: '17:00',
      path: '/retail-village/shops/farm-shop',
      closedOn: [] // No closed days for Farm Shop
    },
    {
      id: 'duchess-collection',
      title: 'The Duchess Collection',
      icon: faCrown,
      image: duchessImage,
      imagePosition: 'center 62%',
      description: 'Discover our exclusive collection of elegant fashion and homeware inspired by the rich heritage of Belvoir Castle. Each piece is carefully curated by the Duchess of Rutland, featuring designs drawn from the castle\'s historic art collection and architectural details.',
      openTime: '09:00',
      closeTime: '17:00',
      path: '/retail-village/shops/duchess-collection',
      closedOn: [] // No closed days for Duchess Collection
    }
  ];

  const otherShops = [
    {
      id: 'belvoir-pet-shop',
      title: 'Belvoir Pet Shop',
      icon: faPaw,
      image: 'https://BelvoirCastle.b-cdn.net/DogShop/Screenshot%202026-08-25%20at%2013.24.14.webp',
      imagePosition: '18% 28%',
      description: 'A boutique dedicated entirely to dogs at Belvoir Retail Village — premium collars, treats, toys and apparel.',
      openTime: '09:00',
      closeTime: '16:00',
      path: '/retail-village/shops/belvoir-pet-shop',
      closedOn: []
    },
    {
      id: 'belvoir-books',
      title: 'Belvoir Books',
      icon: faBook,
      image: belvoirBooksImage,
      imagePosition: 'center top',
      description:
        'Heritage titles from and about Belvoir—memoir, history, landscape, and art. Browse the collection and order for delivery.',
      onlineOnly: true,
      path: '/about/belvoir-books',
      buttonText: 'View books',
      closedOn: []
    },
    {
      id: 'wellness-collection',
      title: 'The Wellness Collection',
      icon: faSpa,
      image: wellnessImage,
      imagePosition: 'center 46%',
      description:
        'A carefully curated edit of some of the very best in wellness, brought together in one considered collection. From yoga and Pilates accessories to natural activewear, clean bath and body, mood-enhancing natural fragrances and nourishing snacks and supplements — discover beautiful brands and products chosen specifically to support you — body and mind.',
      comingSoon: true,
      externalUrl: WELLNESS_SITE_URL,
      buttonText: 'Visit Belvoir Wellness',
      closedOn: []
    }
  ];

  return (
    <ShopsContainer>
      <Hero>
        <HeroImage src={retailBanner} alt="" />
        <HeroOverlay />
        <HeroInner>
          <HeroCopy>
            <HeroTitle>Our Shops</HeroTitle>
            <HeroSubtitle>A unique shopping destination in historic surroundings</HeroSubtitle>
          </HeroCopy>
          <ExploreButton
            href="#shops"
            onClick={(event) => {
              event.preventDefault();
              const target = document.getElementById('shops');
              if (!target) return;
              const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
            }}
          >
            Explore the shops
          </ExploreButton>
        </HeroInner>
      </Hero>
      
      <ContentContainer id="shops">
        <CurrentTimeDisplay $status={retailVillage.status} aria-live="polite">
          <TimeStatus>
            <StatusDot $status={retailVillage.status} />
            <StatusCopy>
              <StatusLabel $status={retailVillage.status}>{retailVillage.label}</StatusLabel>
              <StatusNote>{retailVillage.note}</StatusNote>
            </StatusCopy>
          </TimeStatus>
          <TimeValue $status={retailVillage.status}>
            {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </TimeValue>
        </CurrentTimeDisplay>

        <FeaturedShops>
          {featuredShops.map(shop => (
            <FeaturedShopCard key={shop.id}>
              <FeaturedShopImage $position={shop.imagePosition} $fit={shop.imageFit}>
                <img src={shop.image} alt="" />
                <StatusBadge isOpen={isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen} openingSoon={isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon}>
                  <FontAwesomeIcon icon={faClock} />
                  {isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen ? 'Open Now' : isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon ? 'Opening Soon' : 'Closed'}
                </StatusBadge>
              </FeaturedShopImage>
              <ShopContent>
                <ShopTitle>
                  <FontAwesomeIcon icon={shop.icon} />
                  {shop.title}
                </ShopTitle>
                <ShopDescription>{shop.description}</ShopDescription>
                <ShopFooter>
                  <InfoItem>
                    <FontAwesomeIcon icon={faClock} />
                    {shop.openTime} - {shop.closeTime}
                  </InfoItem>
                  <ButtonContainer>
                    <Button onClick={() => navigate(shop.path)}>{shop.buttonText || 'Visit Shop Page'}</Button>
                  </ButtonContainer>
                </ShopFooter>
              </ShopContent>
            </FeaturedShopCard>
          ))}
        </FeaturedShops>

        <OtherShops>
          {otherShops.map(shop => (
            <ShopCard key={shop.id}>
              <ShopImage $position={shop.imagePosition} $fit={shop.imageFit}>
                <img src={shop.image} alt="" />
                <StatusBadge
                  $comingSoon={shop.comingSoon}
                  isOpen={shop.onlineOnly || (!shop.comingSoon && isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen)}
                  openingSoon={!shop.onlineOnly && !shop.comingSoon && isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon}
                >
                  <FontAwesomeIcon icon={shop.comingSoon ? faClock : shop.onlineOnly ? faGlobe : faClock} />
                  {shop.comingSoon
                    ? 'Coming Soon'
                    : shop.onlineOnly
                      ? 'Online only'
                      : isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen
                        ? 'Open Now'
                        : isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon
                          ? 'Opening Soon'
                          : 'Closed'}
                </StatusBadge>
              </ShopImage>
              <ShopContent>
                <ShopTitle>
                  <FontAwesomeIcon icon={shop.icon} />
                  {shop.title}
                </ShopTitle>
                <ShopDescription>{shop.description}</ShopDescription>
                <ShopFooter>
                  {!shop.comingSoon && (
                    <InfoItem>
                      <FontAwesomeIcon icon={shop.onlineOnly ? faGlobe : faClock} />
                      {shop.onlineOnly
                        ? 'Online shop only — order for delivery'
                        : `${shop.openTime} - ${shop.closeTime}`}
                    </InfoItem>
                  )}
                  <ButtonContainer>
                    <Button
                      onClick={() => {
                        if (shop.externalUrl) {
                          window.open(shop.externalUrl, '_blank', 'noopener,noreferrer');
                          return;
                        }
                        navigate(shop.path);
                      }}
                    >
                      {shop.buttonText || 'Visit Shop Page'}
                    </Button>
                  </ButtonContainer>
                </ShopFooter>
              </ShopContent>
            </ShopCard>
          ))}
        </OtherShops>
        
        {/* <UpcomingDatesSection>
          <UpcomingDatesTitle>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Upcoming Important Dates for Shops
          </UpcomingDatesTitle>
          <DatesList>
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Normal Opening Hours
              </DateTitle>
              <DateRange>Daily (except special dates below)</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 5:00pm daily<br/>
                <strong>Other Retail Shops:</strong> Check individual shop pages for hours
              </DateDetails>
            </DateCard>
            
            <DateCard $type="closed">
              <DateTitle>
                <FontAwesomeIcon icon={faTimes} />
                Christmas Day
              </DateTitle>
              <DateRange>Thursday 25th December 2025</DateRange>
              <DateDetails>
                <strong>All shops closed</strong>
              </DateDetails>
              <DateNote>We will be closed for Christmas Day</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Boxing Day
              </DateTitle>
              <DateRange>Friday 26th December 2025</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 3:30pm<br/>
                <strong>Other shops:</strong> Closed
              </DateDetails>
              <DateNote>Special Boxing Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                New Year's Day
              </DateTitle>
              <DateRange>Thursday 1st January 2026</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 3:30pm<br/>
                <strong>Other shops:</strong> Closed
              </DateDetails>
              <DateNote>Special New Year's Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faCheck} />
                January - March 2026
              </DateTitle>
              <DateRange>1st January - 14th March 2026</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 5:00pm daily<br/>
                <strong>Retail Village:</strong> 9:00am - 5:00pm<br/>
                <strong>Other shops:</strong> Check individual shop pages
              </DateDetails>
              <DateNote>Normal opening hours resume after New Year's Day</DateNote>
            </DateCard>
          </DatesList>
        </UpcomingDatesSection> */}
      </ContentContainer>
      
      <Footer />
    </ShopsContainer>
  );
};

export default ShopsOverview; 